/**
 * Centralized logging system for auth, session, and error tracking
 * Logs are persisted to localStorage for debugging
 */

export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: string;
  message: string;
  data?: any;
}

const MAX_LOGS = 500; // Keep last 500 logs
const STORAGE_KEY = 'app_debug_logs';

class Logger {
  private logs: LogEntry[] = [];

  constructor() {
    this.loadLogs();
    this.setupGlobalErrorHandlers();
  }

  private loadLogs() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    }
  }

  private saveLogs() {
    try {
      // Keep only last MAX_LOGS entries
      if (this.logs.length > MAX_LOGS) {
        this.logs = this.logs.slice(-MAX_LOGS);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Failed to save logs:', error);
    }
  }

  private addLog(level: LogLevel, category: string, message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data
    };

    this.logs.push(entry);
    this.saveLogs();

    // Also log to console with color coding
    const color = {
      info: '#3b82f6',
      warn: '#f59e0b',
      error: '#ef4444',
      debug: '#8b5cf6'
    }[level];

    console.log(
      `%c[${category}] ${message}`,
      `color: ${color}; font-weight: bold;`,
      data || ''
    );
  }

  // Auth events
  auth(message: string, data?: any) {
    this.addLog('info', 'AUTH', message, data);
  }

  // Session events
  session(message: string, data?: any) {
    this.addLog('info', 'SESSION', message, data);
  }

  // Network events
  network(message: string, data?: any) {
    this.addLog('info', 'NETWORK', message, data);
  }

  // Activity tracking
  activity(message: string, data?: any) {
    this.addLog('debug', 'ACTIVITY', message, data);
  }

  // Error logging
  error(message: string, error?: any) {
    this.addLog('error', 'ERROR', message, {
      error: error?.message || error,
      stack: error?.stack
    });
  }

  // Warning logging
  warn(message: string, data?: any) {
    this.addLog('warn', 'WARNING', message, data);
  }

  // Get all logs
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
    localStorage.removeItem(STORAGE_KEY);
  }

  // Export logs as JSON
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Setup global error handlers
  private setupGlobalErrorHandlers() {
    // Catch unhandled errors
    window.onerror = (message, source, lineno, colno, error) => {
      this.error('Unhandled error', {
        message,
        source,
        lineno,
        colno,
        error: error?.message,
        stack: error?.stack
      });
      return false; // Let default handler run too
    };

    // Catch unhandled promise rejections
    window.onunhandledrejection = (event) => {
      this.error('Unhandled promise rejection', {
        reason: event.reason,
        promise: event.promise
      });
    };

    // Track visibility changes
    document.addEventListener('visibilitychange', () => {
      this.session('Visibility changed', {
        hidden: document.hidden,
        visibilityState: document.visibilityState
      });
    });

    // Track online/offline
    window.addEventListener('online', () => {
      this.network('Network online');
    });

    window.addEventListener('offline', () => {
      this.network('Network offline');
    });

    // Track focus/blur
    window.addEventListener('focus', () => {
      this.session('Window focused');
    });

    window.addEventListener('blur', () => {
      this.session('Window blurred');
    });

    // Detect sleep/wake (time drift detection)
    let lastTime = Date.now();
    setInterval(() => {
      const currentTime = Date.now();
      const timeDrift = currentTime - lastTime - 5000; // Expected 5s interval
      
      if (timeDrift > 10000) {
        // More than 10s drift = likely sleep/wake
        this.session('System wake detected', {
          driftMs: timeDrift,
          lastTime: new Date(lastTime).toISOString(),
          currentTime: new Date(currentTime).toISOString()
        });
      }
      
      lastTime = currentTime;
    }, 5000);
  }
}

// Singleton instance
export const logger = new Logger();
